import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NumericFormat as NumberFormat } from "react-number-format";
import { NavBarLateral } from "../NavBarLateral";
import { busquedaActions } from "../../../_actions/busqueda.actions";
import ofertasService, { ALCANCE, ESTADO_OFERTA, TIPO_OFERTA } from "../../../_services/cart/ofertas.service";
import alertService from "../../../_services/cart/alert.service";
import { OfertaForm } from "./OfertaForm";
import { SelectorProductos } from "./SelectorProductos";

/**
 * Las ofertas del vendedor.
 *
 * <h3>Tarjetas, no tabla</h3>
 * Una oferta no es una fila de datos homogéneos: tiene un nombre, una regla que se lee como una
 * frase ("todos los repuestos de la baja Focus 2012"), una vigencia y un interruptor. En una tabla,
 * la columna "alcance" tendría que decir `MARCA_MODELO` y la de criterios quedaría vacía en dos de
 * cada tres filas. En tarjeta cada oferta se lee como lo que es.
 *
 * <h3>El estado se filtra con solapas y no se guarda</h3>
 * `VIGENTE / PROGRAMADA / VENCIDA / PAUSADA` sale de cruzar el interruptor con las fechas, y lo
 * calcula el back al leer. Guardarlo obligaría a un job de medianoche que moviera las ofertas solo,
 * y a vivir con la ventana en la que todavía no corrió.
 */

const SOLAPAS = [
    { key: "TODAS", titulo: "Todas" },
    { key: ESTADO_OFERTA.VIGENTE, titulo: "Corriendo" },
    { key: ESTADO_OFERTA.PROGRAMADA, titulo: "Programadas" },
    { key: ESTADO_OFERTA.PAUSADA, titulo: "Pausadas" },
    { key: ESTADO_OFERTA.VENCIDA, titulo: "Vencidas" },
];

const TONO_ESTADO = {
    [ESTADO_OFERTA.VIGENTE]: "ok",
    [ESTADO_OFERTA.PROGRAMADA]: "espera",
    [ESTADO_OFERTA.PAUSADA]: "apagada",
    [ESTADO_OFERTA.VENCIDA]: "vencida",
};

const ETIQUETA_ESTADO = {
    [ESTADO_OFERTA.VIGENTE]: "Corriendo",
    [ESTADO_OFERTA.PROGRAMADA]: "Programada",
    [ESTADO_OFERTA.PAUSADA]: "Pausada",
    [ESTADO_OFERTA.VENCIDA]: "Vencida",
};

/** "2026-09-30" → "30/09/2026". Se parte el string: `new Date` de un ISO corre un día en UTC-3. */
const fecha = (valor) => {
    if (!valor) return "";
    const [anio, mes, dia] = String(valor).split("-");
    return dia ? `${dia}/${mes}/${anio}` : valor;
};

/**
 * La regla de la oferta contada como una frase.
 *
 * <p>El vendedor no piensa en "alcance = MARCA_MODELO, marcaId = 4": piensa "20% en los paragolpes
 * de Ford". Traducir el modelo de datos a esa frase es lo que hace que el listado se pueda barrer
 * de un vistazo en vez de tener que abrir cada oferta para recordar qué hacía.
 */
const describirAlcance = (oferta) => {
    if (oferta.alcance === ALCANCE.BAJA) {
        const baja = oferta.productoBaja;
        const auto = [baja?.marca?.nombre, baja?.modelo?.nombre, baja?.anio].filter(Boolean).join(" ");
        return `Toda la baja ${auto || `#${baja?.id ?? ""}`}`;
    }

    if (oferta.alcance === ALCANCE.MARCA_MODELO) {
        const partes = [
            oferta.tipoRepuesto?.nombre,
            oferta.marca?.nombre,
            oferta.modelo?.nombre,
            oferta.version?.nombre,
        ].filter(Boolean);

        return partes.length ? partes.join(" · ") : "Todo el catálogo";
    }

    return `${oferta.productos || 0} publicaciones elegidas`;
};

const describirDescuento = (oferta) => {
    if (oferta.tipo === TIPO_OFERTA.PORCENTAJE) return `${Number(oferta.valor)}% off`;
    if (oferta.tipo === TIPO_OFERTA.IMPORTE_FIJO) {
        return (
            <>
                <NumberFormat prefix="$" value={oferta.valor} displayType="text" thousandSeparator="." decimalSeparator="," decimalScale={0} />
                {" de descuento"}
            </>
        );
    }
    return (
        <>
            {"Precio final "}
            <NumberFormat prefix="$" value={oferta.valor} displayType="text" thousandSeparator="." decimalSeparator="," decimalScale={0} />
        </>
    );
};

const OfertasListBase = ({ filtros, traerFiltros }) => {
    const [solapa, setSolapa] = useState("TODAS");
    const [texto, setTexto] = useState("");
    const [ofertas, setOfertas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [editando, setEditando] = useState(undefined); // { id } | { id: null } para el alta
    const [eligiendo, setEligiendo] = useState(undefined);

    useEffect(() => {
        if (filtros === undefined) traerFiltros();
    }, [filtros, traerFiltros]);

    const buscar = useCallback(() => {
        setCargando(true);

        ofertasService
            .buscar({
                page: 0,
                // Sin paginar: un vendedor tiene un puñado de ofertas, no cientos. El día que eso
                // cambie, el back ya devuelve una Page y sólo hay que agregar el paginador.
                pageSize: 200,
                searchValue: texto || null,
                sortOrders: [{ field: "id", direction: "DESC" }],
                options: solapa === "TODAS" ? {} : { estado: solapa },
            })
            .then((resp) => setOfertas(resp.data?.content || []))
            .catch((error) => alertService.error(error))
            .finally(() => setCargando(false));
    }, [solapa, texto]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    const cambiarActiva = (oferta) => {
        ofertasService
            .cambiarActiva(oferta.id, !oferta.activa)
            .then(() => buscar())
            .catch((error) => alertService.error(error));
    };

    const eliminar = (oferta) => {
        /*
         * Un confirm nativo y no un modal propio: es la única acción destructiva de la pantalla y
         * abrir un diálogo con su propio estado para una pregunta de una línea es más código del que
         * la decisión merece.
         */
        if (!window.confirm(`¿Eliminar la oferta “${oferta.nombre}”?`)) return;

        ofertasService
            .eliminar(oferta.id)
            .then(() => {
                alertService.msg("Oferta eliminada", "success");
                buscar();
            })
            .catch((error) => alertService.error(error));
    };

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}} />

            <div className="dashboard__content">
                <div className="dashboard__content-header">
                    <div className="dashboard__content-title">
                        <h2>Ofertas</h2>
                    </div>

                    <button
                        type="button"
                        className="button"
                        data-type="accent"
                        onClick={() => setEditando({ id: null })}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-plus" />
                        Nueva oferta
                    </button>
                </div>

                <p className="panel__bajada">
                    Un descuento, una vigencia y sobre qué corre. El precio tachado aparece solo en el listado,
                    en la ficha y en el carrito, y el descuento se cobra en el checkout.
                </p>

                <div className="ofertas__filtros">
                    <div className="ofertas__solapas">
                        {SOLAPAS.map((s) => (
                            <button
                                key={s.key}
                                type="button"
                                className={s.key === solapa ? "ofertas__solapa ofertas__solapa--activa" : "ofertas__solapa"}
                                onClick={() => setSolapa(s.key)}
                            >
                                {s.titulo}
                            </button>
                        ))}
                    </div>

                    <label className="selector__buscador">
                        <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                        <input
                            type="search"
                            value={texto}
                            placeholder="Buscar por nombre"
                            onChange={(e) => setTexto(e.target.value)}
                        />
                    </label>
                </div>

                {cargando && <p className="panel__estado-carga">Cargando…</p>}

                {!cargando && ofertas.length === 0 && (
                    <div className="panel__vacio">
                        <FontAwesomeIcon icon="fa-solid fa-tags" size="2x" />
                        <p>
                            {solapa === "TODAS"
                                ? "Todavía no creaste ninguna oferta."
                                : "No hay ofertas en este estado."}
                        </p>
                        <span>
                            Una oferta baja el precio de lo que elijas —publicaciones sueltas, un modelo entero o
                            toda una baja— entre dos fechas, y el comprador la ve tachada en la publicación.
                        </span>
                        <button type="button" className="button" data-type="primary" onClick={() => setEditando({ id: null })}>
                            Crear una oferta
                        </button>
                    </div>
                )}

                <div className="ofertas__grilla">
                    {ofertas.map((oferta) => (
                        <article key={oferta.id} className="ofertas__card" data-estado={TONO_ESTADO[oferta.estado]}>
                            <header className="ofertas__card-head">
                                <h3>{oferta.nombre}</h3>
                                <span className="ofertas__estado" data-estado={TONO_ESTADO[oferta.estado]}>
                                    {ETIQUETA_ESTADO[oferta.estado] || oferta.estado}
                                </span>
                            </header>

                            <p className="ofertas__descuento">{describirDescuento(oferta)}</p>

                            <p className="ofertas__alcance">
                                <FontAwesomeIcon
                                    icon={
                                        oferta.alcance === ALCANCE.BAJA
                                            ? "fa-solid fa-car-burst"
                                            : oferta.alcance === ALCANCE.MARCA_MODELO
                                            ? "fa-solid fa-car"
                                            : "fa-solid fa-hand-pointer"
                                    }
                                />
                                {describirAlcance(oferta)}
                            </p>

                            {/*
                                En los alcances por regla el número es el del catálogo de hoy: mañana puede ser
                                otro sin que nadie toque la oferta. Decirlo acá evita la lectura de que son
                                publicaciones "elegidas".
                            */}
                            {oferta.alcance !== ALCANCE.PRODUCTOS && (
                                <p className="ofertas__cuenta">
                                    Alcanza hoy a {oferta.productos || 0}{" "}
                                    {oferta.productos === 1 ? "publicación" : "publicaciones"}
                                </p>
                            )}

                            <p className="ofertas__vigencia">
                                <FontAwesomeIcon icon="fa-regular fa-calendar" />
                                {oferta.hasta
                                    ? `Del ${fecha(oferta.desde)} al ${fecha(oferta.hasta)}`
                                    : `Desde el ${fecha(oferta.desde)}, sin fecha de fin`}
                            </p>

                            {oferta.descripcion && <p className="ofertas__texto">{oferta.descripcion}</p>}

                            <footer className="ofertas__acciones">
                                {oferta.alcance === ALCANCE.PRODUCTOS && (
                                    <button
                                        type="button"
                                        className="button"
                                        data-type="secondary"
                                        onClick={() => setEligiendo(oferta)}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-list-check" />
                                        Publicaciones ({oferta.productos || 0})
                                    </button>
                                )}

                                <button
                                    type="button"
                                    className="button"
                                    data-type="secondary"
                                    onClick={() => setEditando({ id: oferta.id })}
                                >
                                    <FontAwesomeIcon icon="fa-solid fa-pen" />
                                    Editar
                                </button>

                                <button
                                    type="button"
                                    className="button"
                                    data-type="secondary"
                                    title={oferta.activa ? "Pausar" : "Activar"}
                                    onClick={() => cambiarActiva(oferta)}
                                >
                                    <FontAwesomeIcon icon={oferta.activa ? "fa-solid fa-pause" : "fa-solid fa-play"} />
                                    {oferta.activa ? "Pausar" : "Activar"}
                                </button>

                                <button
                                    type="button"
                                    className="button ofertas__eliminar"
                                    data-type="secondary"
                                    title="Eliminar"
                                    onClick={() => eliminar(oferta)}
                                >
                                    <FontAwesomeIcon icon="fa-solid fa-trash" />
                                </button>
                            </footer>
                        </article>
                    ))}
                </div>
            </div>

            {editando && (
                <OfertaForm
                    ofertaId={editando.id}
                    filtros={filtros}
                    onClose={() => setEditando(undefined)}
                    onGuardada={(id, alcance) => {
                        setEditando(undefined);
                        buscar();

                        /*
                         * Una oferta de publicaciones elegidas recién creada no alcanza a nada: elegirlas es
                         * el paso que falta, así que el selector se abre solo en vez de dejar al vendedor
                         * pensando que ya terminó.
                         */
                        if (!editando.id && alcance === ALCANCE.PRODUCTOS && id) {
                            ofertasService
                                .detalle(id)
                                .then((resp) => setEligiendo(resp.data))
                                .catch(() => {});
                        }
                    }}
                />
            )}

            {eligiendo && (
                <SelectorProductos
                    oferta={eligiendo}
                    filtros={filtros}
                    onCambio={buscar}
                    onClose={() => setEligiendo(undefined)}
                />
            )}
        </div>
    );
};

function mapState(state) {
    const { filtros } = state.busqueda;
    return { filtros };
}

const connected = connect(mapState, { traerFiltros: busquedaActions.traerFiltros })(OfertasListBase);
export { connected as OfertasList };
